import { UAParser } from "ua-parser-js";

type KnownBrowserName =
  | "Chrome WebView"
  | "Chrome"
  | "Edge"
  | "Firefox"
  | "IE"
  | "Mobile Chrome"
  | "Mobile Firefox"
  | "Mobile Safari"
  | "Opera"
  | "Safari"
  | "Samsung Internet";

const MINIMUM_SUPPORTED_VERSION: Record<KnownBrowserName, number> = {
  Chrome: 119,
  "Chrome WebView": 119,
  Edge: 119,
  Firefox: 121,
  IE: Infinity,
  "Mobile Chrome": 119,
  "Mobile Firefox": 121,
  "Mobile Safari": 17.4,
  Opera: 105,
  Safari: 17.4,
  "Samsung Internet": 23,
};

const SAFARI_FAMILY = new Set<KnownBrowserName>(["Safari", "Mobile Safari"]);

const BROWSER_UPDATE_URL: Partial<Record<KnownBrowserName, string>> = {
  Chrome: "https://www.google.com/chrome/",
  "Mobile Chrome": "https://www.google.com/chrome/",
  "Chrome WebView": "https://www.google.com/chrome/",
  Edge: "https://www.microsoft.com/edge",
  Firefox: "https://www.mozilla.org/firefox/new/",
  "Mobile Firefox": "https://www.mozilla.org/firefox/new/",
  Safari: "https://support.apple.com/en-us/HT204416",
  "Mobile Safari": "https://support.apple.com/en-us/HT204204",
  Opera: "https://www.opera.com/download",
  "Samsung Internet":
    "https://play.google.com/store/apps/details?id=com.sec.android.app.sbrowser",
};

const FALLBACK_UPDATE_URL = "https://browser-update.org/";

export type BrowserSupportInfo = {
  name: string;
  version: string;
  major: number;
  supported: boolean;
};

function isKnownBrowserName(name: string): name is KnownBrowserName {
  return name in MINIMUM_SUPPORTED_VERSION;
}

export function getBrowserSupportInfo(
  userAgent: string = navigator.userAgent,
): BrowserSupportInfo {
  const {
    name = "",
    version = "",
    major = "",
  } = new UAParser(userAgent).getBrowser();

  if (!isKnownBrowserName(name)) {
    return { name, version, major: Number(major) || 0, supported: true };
  }

  const minimum = MINIMUM_SUPPORTED_VERSION[name];
  const detected = SAFARI_FAMILY.has(name)
    ? parseFloat(version)
    : Number(major);
  const supported = Number.isFinite(detected) && detected >= minimum;

  return { name, version, major: Number(major) || 0, supported };
}

export function getBrowserUpdateUrl(name: string | undefined): string {
  if (name && isKnownBrowserName(name)) {
    return BROWSER_UPDATE_URL[name] ?? FALLBACK_UPDATE_URL;
  }

  return FALLBACK_UPDATE_URL;
}
